/* eslint-disable react/no-array-index-key */
import React, { useCallback, useMemo, useRef } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { pipe, tap } from 'ramda'

import { Box, Typography } from '@mui/material'

import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'

import { TASKS_TAB } from '~/src/Journal/Routes/AllEventsJournal/constants'
import CustomTableRow from '~/src/Journal/Routes/AllEventsJournal/TableRow'
import {
  getEventsDatePeriod,
  groupEventsByAmountWithinDate,
} from '~/src/Journal/Routes/AllEventsJournal/utils'
import createLogger from '~/src/Lib/Logging'
import { datePropType, idPropType } from '~/src/Lib/PropTypes'
import { getDateTime } from '~/src/Lib/Utils'

const displayName = 'Journal/Table'
const logger = createLogger(displayName)
const GUTTER_HEIGHT = 6
const ROW_HEIGHT = 64

const JournalTable = props => {
  const {
    classes,
    eventsToShow,
    journal,
    startIndex,
    tabData,
    onLoadMore,
    setStartIndex,
  } = props
  const listRef = useRef(null)
  const groupedByAmountWithinDates = groupEventsByAmountWithinDate(eventsToShow)
  const isTasksTab = tabData.value === TASKS_TAB
  const flattened = useMemo(() => {
    const flat = groupedByAmountWithinDates.flatMap(events => {
      const period = getEventsDatePeriod(events)
      const [entry, ...rest] = events
      return [{ period }, entry, ...rest]
    })

    return flat
  }, [groupedByAmountWithinDates])
  // TODO: or still this? -> journal.items.length - 2
  const isItemLoaded = index => (journal.items.length && !journal.before) || index < flattened.length - 1

  const Item = useCallback(({
    index,
    style,
  }) => {
    const { [index]: item } = flattened
    const { height: totalHeight, ...styles } = style

    return item.period ? (
      <Typography
        className={classes.eventsDate}
        style={{ ...styles, height: totalHeight - GUTTER_HEIGHT }}
      >
        {item.period}
      </Typography>
    ) : (
      <CustomTableRow
        {...props}
        isTasksTab={isTasksTab}
        item={item.entry ?? item}
        style={{ ...styles, height: totalHeight - GUTTER_HEIGHT }}
      />
    )
  }, [props, classes, flattened, isTasksTab])

  const indexOfFirstEventToday = useMemo(
    () => {
      const endOfToday = getDateTime('now').set({ hour: 23, minute: 59 })
      return isTasksTab ? flattened.findIndex(event => event.timestamp && getDateTime(event.timestamp) <= endOfToday) || 0 : 0
    },
    [isTasksTab, flattened],
  )

  // TODO: I've currently hardcoded some values to have a normal height of each row. Could have been changed to something smarter
  return (
    <AutoSizer>
      {({
        width,
        height,
      }) => (
        <Box
          aria-label={i18n`Journal entries table`}
          component="section"
          id="journal_table"
          style={{ width, height }}
        >
          {/*
            TODO: the height should be definitely adjusted in a different way, not passing 85% like this.
            Also, move all inline styles to styles.js (sorry, haven't time for that).
          */}
          <Box
            aria-label={i18n`Journal entries table`}
            role="table"
            display="grid"
            gridTemplateColumns={isTasksTab ? 'minmax(auto, 30%) repeat(4, 1fr)' : 'minmax(auto, 40%) repeat(3, 1fr)'}
            padding="0 1rem 0.5rem"
            style={{ gap: '1.5rem' }}
          >
            <Box key="title" className={classes.tableHeadCell} role="columnheader">
              <Typography variant="body2" className={classes.headerCell}>
                {i18n`Title`}
              </Typography>
            </Box>
            {isTasksTab ? (
              <Box key="duration" className={classes.tableHeadCell} role="columnheader">
                <Typography variant="body2" className={classes.headerCell}>
                  {i18n`Time worked`}
                </Typography>
              </Box>
            ) : null}
            <Box key="location" className={classes.tableHeadCell} role="columnheader">
              <Typography variant="body2" className={classes.headerCell}>
                {i18n`Location`}
              </Typography>
            </Box>
            <Box key="dueDate" className={classes.tableHeadCell} role="columnheader">
              <Typography variant="body2" className={classes.headerCell}>
                {i18n`Date`}
              </Typography>
            </Box>
            <Box key="createdBy" className={classes.tableHeadCell} role="columnheader">
              <Typography variant="body2" className={classes.headerCell}>
                {i18n`Related user`}
              </Typography>
            </Box>
          </Box>
          <InfiniteLoader
            loadMoreItems={(...args) => {
              logger.debug('[loadMoreItems]', ...args)
              onLoadMore(...args)
            }}
            isItemLoaded={isItemLoaded}
            itemCount={flattened.length}
          >
            {({ onItemsRendered, ref }) => (
              <FixedSizeList
                ref={el => {
                  ref(el)
                  listRef.current = el
                }}
                initialScrollOffset={(ROW_HEIGHT * startIndex) + (ROW_HEIGHT * indexOfFirstEventToday)}
                itemCount={flattened.length}
                itemSize={ROW_HEIGHT}
                height={height - 48}
                width={width}
                onItemsRendered={pipe(
                  tap(({ visibleStartIndex }) => {
                    setStartIndex(visibleStartIndex)
                  }),
                  onItemsRendered
                )}
              >
                {Item}
              </FixedSizeList>
            )}
          </InfiniteLoader>
        </Box>
      )}
    </AutoSizer>
  )
}

const itemShape = PropTypes.shape({
  schema: PropTypes.string,
  timestamp: datePropType,
  uuid: idPropType,
  data: PropTypes.oneOfType([
    PropTypes.shape({ id: idPropType }),
    PropTypes.symbol,
  ]),
})
JournalTable.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  eventsToShow: PropTypes.arrayOf(itemShape).isRequired,
  journal: PropTypes.shape({
    items: PropTypes.arrayOf(itemShape),
    before: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  tabData: PropTypes.shape({
    value: PropTypes.string,
  }).isRequired,
  startIndex: PropTypes.number.isRequired,
  setStartIndex: PropTypes.func.isRequired,
}

JournalTable.displayName = displayName

export default React.memo(JournalTable)
